import { Component, inject, TemplateRef, viewChild, AfterViewInit, signal } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { map, Observable, switchMap, catchError, of, BehaviorSubject, combineLatest } from 'rxjs';
import {
  DataDisplayComponent,
  ButtonComponent,
  DataDisplayItem,
  BadgeComponent
} from 'htx-ui-library';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  ActivityState,
  PartnerDetail
} from '../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../services/organizations/organization.service';
import { NotificationService } from '../../../../services/form/notification.service';
import { ProtectedContentComponent } from '../../../../components/organisms/protected-content/protected-content.component';
import { NavigationService } from '../../../../services/navigation.service';

@Component({
  selector: 'app-partnernetwork-detail-page',
  imports: [
    CommonModule,
    AsyncPipe,
    DataDisplayComponent,
    TranslocoDirective,
    TranslocoPipe,
    ButtonComponent,
    BadgeComponent,
    ProtectedContentComponent
  ],
  templateUrl: './partnernetwork-detail-page.component.html',
  styleUrl: './partnernetwork-detail-page.component.scss'
})
export class PartnerNetworkDetailPageComponent implements AfterViewInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private translocoService = inject(TranslocoService);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private navigationService = inject(NavigationService);

  id$ = this.activatedRoute.paramMap.pipe(map((data) => data.get('id')!));

  requiredRoles = this.navigationService.getAccessRoles('partnernetwork');

  activityStateTemplateRef = viewChild<TemplateRef<any>>('activityStateTemplate');
  didTemplateRef = viewChild<TemplateRef<any>>('didTemplate');
  private activityStateTemplate$ = new BehaviorSubject<TemplateRef<any> | undefined>(undefined);
  private didTemplate$ = new BehaviorSubject<TemplateRef<any> | undefined>(undefined);

  ActivityState = ActivityState;
  activityState: string | undefined;
  did: string | undefined;

  data$: Observable<PartnerDetail> = this.id$.pipe(
    switchMap((id) => this.organizationService.getPartnerDetailsById(id)),
    map((dto) => {
      this.activityState = dto.activityState;
      this.did = dto.did;
      return dto;
    }),
    catchError(() => {
      this.notificationService.showError('partnernetwork.detail.loadingError', 5000);
      return of();
    })
  );

  organizationDetails$: Observable<DataDisplayItem[]> = combineLatest([
    this.data$,
    this.activityStateTemplate$,
    this.didTemplate$
  ]).pipe(
    map(([dto, activityTmpl, didTmpl]) => {
      const addr = dto.address;

      return [
        {
          label: this.translocoService.translate('approval.summary.legalName.label'),
          value: [dto.legalName]
        },
        dto.shortName
          ? {
              label: this.translocoService.translate('approval.summary.shortName.label'),
              value: [dto.shortName]
            }
          : null,
        {
          label: this.translocoService.translate('organization.control.bpnl.label'),
          value: [dto.bpnl ?? '-']
        },
        addr
          ? {
              label: this.translocoService.translate('approval.summary.address.label'),
              value: [`${addr.street} ${addr.houseNumber}`, `${addr.postalCode} ${addr.city}`]
            }
          : null,
        dto.legalForm
          ? {
              label: this.translocoService.translate('organization.edit.controls.legalForm.label'),
              value: [dto.legalForm]
            }
          : null,
        dto.website
          ? {
              label: this.translocoService.translate('approval.summary.website.label'),
              value: [dto.website]
            }
          : null,
        dto.description
          ? {
              label: this.translocoService.translate('approval.summary.description.label'),
              value: [dto.description]
            }
          : null,
        dto.taxIdNumber
          ? {
              label: this.translocoService.translate('approval.summary.taxIdNumber.label'),
              value: [dto.taxIdNumber]
            }
          : null,
        dto.commercialRegisterNumber
          ? {
              label: this.translocoService.translate(
                'approval.summary.commercialRegisterNumber.label'
              ),
              value: [dto.commercialRegisterNumber]
            }
          : null,
        dto.commercialRegisterCourt
          ? {
              label: this.translocoService.translate(
                'approval.summary.commercialRegisterCourt.label'
              ),
              value: [dto.commercialRegisterCourt]
            }
          : null,
        {
          label: this.translocoService.translate('organization.control.activityState.label'),
          ...(activityTmpl ? { template: activityTmpl } : { value: [dto.activityState ?? '-'] })
        },
        {
          label: this.translocoService.translate('approval.summary.did.label'),
          ...(didTmpl && dto.did ? { template: didTmpl } : { value: [dto.did ?? '-'] })
        },
        dto.representativeName
          ? {
              label: this.translocoService.translate('partnernetwork.detail.representativeName'),
              value: [dto.representativeName]
            }
          : null,
        dto.representativeEmail
          ? {
              label: this.translocoService.translate('partnernetwork.detail.representativeEmail'),
              value: [dto.representativeEmail]
            }
          : null
      ].filter(Boolean) as DataDisplayItem[];
    })
  );

  ngAfterViewInit(): void {
    this.activityStateTemplate$.next(this.activityStateTemplateRef());
    this.didTemplate$.next(this.didTemplateRef());
  }

  backToList(): void {
    this.router.navigate(['/partnernetwork']);
  }
}
